import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FaShieldAlt,
  FaBullseye,
  FaCode,
  FaPlay,
  FaExclamationTriangle,
  FaChevronRight,
  FaInfoCircle,
  FaChartLine,
  FaCalendarAlt,
} from 'react-icons/fa';

export type IntegrityTrustLevel = 'trusted' | 'suspicious' | 'cheater';

type Props = {
  trustLevel: IntegrityTrustLevel;
  t: (key: string) => string;
};

export function AnticheatSettings({ trustLevel, t }: Props) {

  const trustMeta = useMemo(() => {
    if (trustLevel === 'suspicious') {
      return {
        statusTitle: t('settings.anticTrustStatusSuspicious'),
        statusSub: t('settings.anticTrustHasSignals'),
        shieldClass: 'settings-antic-shield--warn',
      };
    }
    if (trustLevel === 'cheater') {
      return {
        statusTitle: t('settings.anticTrustStatusHighRisk'),
        statusSub: t('settings.anticTrustSeriousSignals'),
        shieldClass: 'settings-antic-shield--bad',
      };
    }
    return {
      statusTitle: t('settings.anticTrustStatusOk'),
      statusSub: t('settings.anticTrustNoSuspicion'),
      shieldClass: 'settings-antic-shield--ok',
    };
  }, [trustLevel, t]);

  const signalStatuses = useMemo(() => {
    if (trustLevel === 'cheater') {
      return [
        t('settings.anticSignalStatusWarn'),
        t('settings.anticSignalStatusWarn'),
        t('settings.anticSignalStatusWarn'),
        t('settings.anticSignalStatusAlert'),
      ];
    }
    if (trustLevel === 'suspicious') {
      return [
        t('settings.anticSignalStatusNormal'),
        t('settings.anticSignalStatusNote'),
        t('settings.anticSignalStatusNormal'),
        t('settings.anticSignalStatusNote'),
      ];
    }
    return [
      t('settings.anticSignalStatusNormal'),
      t('settings.anticSignalStatusClear'),
      t('settings.anticSignalStatusNormal'),
      t('settings.anticSignalStatusNoAlerts'),
    ];
  }, [trustLevel, t]);

  const signalCards = useMemo(() => {
    const base = [
      {
        icon: <FaBullseye />,
        titleKey: 'settings.anticSignalAttention',
        descKey: 'settings.anticSignalAttentionDesc',
        status: signalStatuses[0],
      },
      {
        icon: <FaCode />,
        titleKey: 'settings.anticSignalCode',
        descKey: 'settings.anticSignalCodeDesc',
        status: signalStatuses[1],
      },
      {
        icon: <FaPlay />,
        titleKey: 'settings.anticSignalRuns',
        descKey: 'settings.anticSignalRunsDesc',
        status: signalStatuses[2],
      },
      {
        icon: <FaExclamationTriangle />,
        titleKey: 'settings.anticSignalIntegrity',
        descKey: 'settings.anticSignalIntegrityDesc',
        status: signalStatuses[3],
      },
    ];

    const tones: Array<'ok' | 'warn' | 'bad'> =
      trustLevel === 'cheater'
        ? ['warn', 'warn', 'warn', 'bad']
        : trustLevel === 'suspicious'
          ? ['ok', 'warn', 'ok', 'warn']
          : ['ok', 'ok', 'ok', 'ok'];

    return base.map((card, i) => ({ ...card, tone: tones[i] }));
  }, [signalStatuses, trustLevel]);

  const dynamicsMetrics = useMemo(() => {
    if (trustLevel === 'cheater') {
      return { solutions: 96, alerts: 7, daysStable: 4 };
    }
    if (trustLevel === 'suspicious') {
      return { solutions: 112, alerts: 2, daysStable: 9 };
    }
    return { solutions: 128, alerts: 0, daysStable: 23 };
  }, [trustLevel]);

  return (
    <div className="settings-container settings-antic">
      <div className="settings-antic-head">
        <div>
          <h1 className="settings-title">{t('settings.sectionAnticheat')}</h1>
          <p className="settings-subtitle settings-antic-intro">{t('settings.anticIntro')}</p>
        </div>
        <Link to="/anticheat-policy" className="settings-antic-how-link">
          <FaInfoCircle aria-hidden />
          {t('settings.anticHowItWorks')}
        </Link>
      </div>

      <div className="settings-antic-trust-grid">
        <div className={`settings-antic-card settings-antic-status-card is-${trustLevel}`}>
          <h2 className="settings-antic-card__title">{t('settings.anticTrustTitle')}</h2>
          <div className="settings-antic-status-body">
            <div className={`settings-antic-shield ${trustMeta.shieldClass}`} aria-hidden>
              <FaShieldAlt />
              <span className="settings-antic-shield__check">✓</span>
            </div>
            <div className="settings-antic-status-text">
              <div className={`settings-antic-status-text__title is-${trustLevel}`}>{trustMeta.statusTitle}</div>
              <div className="settings-antic-status-text__sub">{trustMeta.statusSub}</div>
            </div>
          </div>
          <div className="settings-antic-status-text__sub settings-antic-last-check">
            {t('settings.anticLastCheck')}: 14.07.2025, 10:34
          </div>
        </div>

        <div className="settings-antic-card settings-antic-dynamics-card">
          <h2 className="settings-antic-card__title settings-antic-dynamics-title">
            {t('settings.anticDynamicsTitle')}
            <FaInfoCircle className="settings-antic-dynamics-title__info" aria-hidden />
          </h2>

          <div className="settings-antic-dynamics-metrics">
            <div className="settings-antic-dynamics-metric">
              <FaChartLine className="settings-antic-dynamics-metric__icon" aria-hidden />
              <div>
                <div className="settings-antic-dynamics-metric__value">{dynamicsMetrics.solutions}</div>
                <div className="settings-antic-dynamics-metric__label">{t('settings.anticDynamicsSolutions')}</div>
              </div>
            </div>
            <div className="settings-antic-dynamics-metric">
              <FaShieldAlt className="settings-antic-dynamics-metric__icon" aria-hidden />
              <div>
                <div className="settings-antic-dynamics-metric__value">{dynamicsMetrics.alerts}</div>
                <div className="settings-antic-dynamics-metric__label">{t('settings.anticDynamicsAlerts')}</div>
              </div>
            </div>
            <div className="settings-antic-dynamics-metric">
              <FaCalendarAlt className="settings-antic-dynamics-metric__icon" aria-hidden />
              <div>
                <div className="settings-antic-dynamics-metric__value">
                  {dynamicsMetrics.daysStable} {t('settings.anticDynamicsDaysUnit')}
                </div>
                <div className="settings-antic-dynamics-metric__label">{t('settings.anticDynamicsStable')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="settings-antic-section">
        <div className="settings-antic-section__head">
          <h2 className="settings-antic-section__title">{t('settings.anticSignalsTitle')}</h2>
          <Link to="/anticheat-policy" className="settings-antic-section__more">
            {t('settings.anticSignalsMore')}
          </Link>
        </div>
        <div className="settings-antic-signals">
          {signalCards.map((card) => (
            <div key={card.titleKey} className="settings-antic-signal-card">
              <span className="settings-antic-signal-card__icon" aria-hidden>{card.icon}</span>
              <div className="settings-antic-signal-card__title">{t(card.titleKey)}</div>
              <div className="settings-antic-signal-card__desc">{t(card.descKey)}</div>
              <div className={`settings-antic-signal-card__status is-${card.tone}`}>
                {card.status}
                <FaChevronRight aria-hidden />
              </div>
            </div>
          ))}
        </div>
        <p className="settings-antic-signals-footer">{t('settings.anticSignalsFooter')}</p>
      </section>
    </div>
  );
}
